import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * Default price (in cents) at or above which "Call for Price" is shown instead of the
 * price. $50,000.00 => 5,000,000 cents.
 *
 * Override per element with a `data-call-for-price-threshold` attribute (in cents) on the
 * <product-price> tag, and override the label text with `data-call-for-price-text`.
 * @type {number}
 */
const DEFAULT_CALL_FOR_PRICE_THRESHOLD = 5000000;

/** @type {string} */
const DEFAULT_CALL_FOR_PRICE_TEXT = 'Call for Price';

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);

    // No variant update event fires on first load, so evaluate the initial price here.
    this.maybeShowCallForPrice();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * The price threshold (in cents) at/above which the price is hidden in favor of "Call for Price".
   * @returns {number}
   */
  get callForPriceThreshold() {
    const parsed = Number.parseInt(this.dataset.callForPriceThreshold ?? '', 10);
    return Number.isFinite(parsed) ? parsed : DEFAULT_CALL_FOR_PRICE_THRESHOLD;
  }

  /**
   * The label shown when a variant is at/above the threshold.
   * @returns {string}
   */
  get callForPriceText() {
    return this.dataset.callForPriceText || DEFAULT_CALL_FOR_PRICE_TEXT;
  }

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const { priceContainer, volumePricingNote } = this.refs;
    // Find the new product-price element in the updated HTML
    const newProductPrice = event.detail.data.html.querySelector(
      `product-price[data-block-id="${this.dataset.blockId}"]`
    );
    if (!newProductPrice) return;

    // Update price container
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    if (newPrice && priceContainer) {
      priceContainer.replaceWith(newPrice);
    }

    // Update volume pricing note
    const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');

    if (!newNote) {
      volumePricingNote?.remove();
    } else if (!volumePricingNote) {
      // Use newPrice since priceContainer was just replaced and now points to the detached element
      newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      volumePricingNote.replaceWith(newNote);
    }

    // Update installments (SPI banner) variant ID to trigger payment terms re-render
    const input_selector = `#product-form-installment-${this.dataset.blockId} input[name="id"]`;
    const installmentsInput = /** @type {HTMLInputElement|null} */ (this.querySelector(input_selector));
    if (installmentsInput) {
      installmentsInput.value = event.detail.resource?.id ?? '';
      installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // The price container was just re-rendered with the new variant's price, so
    // re-evaluate whether this variant should show "Call for Price".
    this.maybeShowCallForPrice();
  };

  /**
   * Resolve the currently displayed variant price in cents.
   *
   * Prefers an explicit `data-amount="{{ variant.price }}"` (cents) on the price container
   * when the Liquid template provides one; otherwise falls back to parsing the rendered
   * price text. The text fallback assumes a single dot-decimal currency (e.g. USD) and is
   * best-effort only — add the data attribute for reliable, locale-proof behavior.
   * @returns {number|null}
   */
  getPriceInCents() {
    const { priceContainer } = this.refs;
    if (!priceContainer) return null;

    // Reliable path: cents supplied by the template.
    const dataAmount = Number.parseInt(priceContainer.dataset.amount ?? '', 10);
    if (Number.isFinite(dataAmount)) return dataAmount;

    // Fallback: parse the formatted price text.
    const text = (priceContainer.textContent ?? '').replace(/[^\d.,]/g, '');
    if (!text) return null;

    const amount = Number.parseFloat(text.replace(/,/g, ''));
    return Number.isFinite(amount) ? Math.round(amount * 100) : null;
  }

  /**
   * If the current variant price meets or exceeds the threshold, replace the price display
   * with the "Call for Price" label. Below the threshold, the server-rendered price (already
   * in place) is left untouched.
   */
  maybeShowCallForPrice() {
    const { priceContainer, volumePricingNote } = this.refs;
    if (!priceContainer) return;

    const priceInCents = this.getPriceInCents();
    if (priceInCents == null || priceInCents < this.callForPriceThreshold) return;

    const message = document.createElement('span');
    message.className = 'price__call-for-price';
    message.textContent = this.callForPriceText;

    priceContainer.replaceChildren(message);
    priceContainer.classList.add('price--call-for-price');

    // A volume-pricing note is meaningless without a displayed price.
    volumePricingNote?.remove();
  }
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
